import type { components } from "~/types/entity/emerIntwcApiWeb.schema";
import type { PrintType } from "~/types/entity/printModel";

/**
 * ページ種別 (権限確認 としても利用される)
 */
export type MainViewMode = "CIRCLE_LIST" | "BOOTH_LIST" | "FAVORITE" | "FAVORITE_BOOTH" | "COMIKE_MAP" | "COMIKE_BOOTH_MAP" | "COMIKE_PRINT" | "TOP_PAGE" |
"CIRCLE_DETAIL" | "BOOTH_DETAIL" | "COMIKE_PRINT_DETAIL" | "COMIKE_PRINT_PAPERINDEX" | "LOGIN" | "LOGOUT" | "RESTRICTION" | "NO_ACTIVE_USER" | "HELP" | "PAYMENT_START" | "PAYMENT_PROCESS" | "PAYMENT_CANCEL" | "PAYMENT_SELECT_PLAN" | "COMICOS"
 | "TODAY" | "WEBCATALOG_CLASSIC" | "WEBCATALOG_CLASSIC_BILLING" | "COMICOS_MS" | "LP" | "INQUIRY"
 | "IMPORT_EXPORT" | "PIXIV_IMPORT" | "PIXIV_EXPORT"
export type Circle = components["schemas"]["ICircleDetail"]
export type EventDay = components["schemas"]["Day"]
export type MapArea = "e123" | "e456" | "e7" | "e78" | "w12" | "s12" | "c1" | "c2"
export type Area = components["schemas"]["Area"]
export type GenreLocation = {
  code: string
  day: number
  area: string
  space: string
}
export type GenreSelectItem = /* VSelectItem & */ {
  // v-select に表示するためのプロパティ名割り当て 件数は限られているので算出値を保持する
  title: string
  subtitle?: string
  value: string /* 検索上の ジャンルコードは文字列管理である | number */
  genreLocation: GenreLocation
}
export type GenreSelectItemList = GenreSelectItem[]

export type EventInfo = components["schemas"]["Eventinfo"]
export type FavoriteCircle = components["schemas"]["CircleFavorite"]
export type ICircleJyunbikai = components["schemas"]["ICircleJyunbikai"];
/**
 * Event/Base の OpenAPI schema では hallNumber が公開されていないため、Map Geo からデータ層で補完する。
 */
export type ICircleJyunbikaiWithHallNumber = ICircleJyunbikai & {
  hallNumber: string | null | undefined
};
export type ICircleIdSearchItem = components["schemas"]["CircleIdSearchItem"];
export type FavoriteColorName = components["schemas"]["FavoriteColorName"];
export type FavoriteUnknown = components["schemas"]["FavoriteUnknown"];
export type ExtraSpace = components["schemas"]["ExtraSpace"][];
export type ICircleFavorite = ICircleJyunbikai & {
  // お気に入り一覧表示用の追加プロパティ (生成タイミングで必須にする)
  writer: string
  webCircleImages?: components["schemas"]["ICircleImage"][] | null
  movies?: components["schemas"]["ICircleImage"][] | null
  hallNumber?: string | null
  lastUpdateAt?: number | null
  activities?: components["schemas"]["IActivityItem"][] | null
};

export type CircleFavoritePair = {
  circleJyunbikai: ICircleJyunbikaiWithHallNumber
  favorite: FavoriteCircle
  circleFavorite: ICircleFavorite | undefined
};

export type EventBaseState = {
  /**
   * 更新日時 この値が一致していれば WebAPI 問い合わせを抑制するために使う
   */
  lastModified: number
  acceptLanguage: string
  eventBase: components["schemas"]["V2EventBaseResponseBody"] | undefined
  genreSelectItemList: GenreSelectItemList
  lookupMapAreaByBlock: Map<string, MapArea>
  lookupCircleJyunbikaiByWcid: Map<number, ICircleJyunbikaiWithHallNumber>
  lookupBookletCircleJyunbikaiByPage: Map<number, ICircleJyunbikaiWithHallNumber[]>
  bookletBlockMap: Map<string,Map<string,number>>
  extraSpaceMap: Map<string,ExtraSpaceCircle>
  bookletWcidToPageMap: Map<number, number>
}

/**
 * EventBaseState を「1 階層だけ深く」readonly 化した型 (#33449 再発防止)。
 * トップおよびコンテナ (Array/Map) の in-place 変更 (.sort/.push/.set 等) は
 * 型エラーにするが、要素 (Circle/ICircleJyunbikai 等) 自体の書き換えは型上許容する。
 * これにより 2〜3万件の要素に対する再帰的 readonly 化を避け、
 * ランタイムは shallowReadonly(state) のまま Proxy を要素に張らずに済む。
 */
export type ReadonlyEventBaseState = {
  readonly lastModified: EventBaseState["lastModified"]
  readonly acceptLanguage: EventBaseState["acceptLanguage"]
  readonly eventBase: EventBaseState["eventBase"] extends infer B
    ? { readonly [K in keyof B]:
        B[K] extends ReadonlyArray<infer U> ? ReadonlyArray<U> :
        B[K] extends Array<infer U> ? ReadonlyArray<U> :
        B[K] extends Map<infer MK, infer MV> ? ReadonlyMap<MK, MV> :
        B[K] }
    : never
  readonly genreSelectItemList: ReadonlyArray<GenreSelectItem>
  readonly lookupMapAreaByBlock: ReadonlyMap<string, MapArea>
  readonly lookupCircleJyunbikaiByWcid: ReadonlyMap<number, ICircleJyunbikaiWithHallNumber>
  readonly lookupBookletCircleJyunbikaiByPage: ReadonlyMap<number, ReadonlyArray<ICircleJyunbikaiWithHallNumber>>
  readonly bookletBlockMap: ReadonlyMap<string, ReadonlyMap<string, number>>
  readonly extraSpaceMap: ReadonlyMap<string, ExtraSpaceCircle>
  readonly bookletWcidToPageMap: Map<number, number>
}

/**
 * "/api/v3/Circle/Search" の結果を加工して返している
 * TODO: 将来的には削除したい。個別に代入する理由がない
 */
type CircleListWithPage = {
  circles: Array<Circle>
  page: number
  maxPage: number
  count: number
  maxCount: number
}
export type PricingPlan = components["schemas"]["PricingPlan"]
export enum PricingPlanRequestMediaCode {
  Webcatalog = 1, // 通常WebCatalog
  AndroidApp = 2, // Android
  IntWebCatalog = 3, // 国際版アプリ
  WebCatalog2026 = 4, // WebCatalog2026 (日本版リニューアル)
}

/**
 * WebAPI 側にこの定義がない
 */
export type GetSelectPlanResult = {
  status: number
  common: {
    popClientKey: string
    success_url: string
    incomplete_url: string
    failure_url: string
    push_url: string
  }
  vtPaymentKeyResponse: {
    payment_key: string
    result_code: string
    status: string
    message: string
    payment_key_expiry_time: string
  }
  vtPaymentAcctmgmtResonse: {
    account_mgmt_key?: string
    result_code: string
    status: string
    message: string
    account_mgmt_key_expiry_time?: string
  } | null
}
export type BillingState = components["schemas"]["BillingState"]

/**
 * Emer.IntwcApi/Emer.IntwcApi.Core/Models/Succession/請求ステータスenum.cs
 */
export enum InvoiceState {
  Unknown = 0,
  支払待ち = 1,
  自動請求待ち = 2,
  支払完了 = 3,
  ユーザーキャンセル = 4,
  システムキャンセル = 5,
  コンビニ支払待ち = 6,
  コンビニ支払期限切れ = 7,
  期限切れ = 8,
  カード情報更新 = 9,
  カード情報更新待ち = 10,
  返金 = 11,
  解約 = 12,
  自動請求エラー = 13,
  解約キャンセル = 14,
}

/**
 * Emer.IntwcApi/Emer.IntwcApi.Core/Models/Succession/請求ステータスenum.cs
 * 契約ステータスenum
 */
export enum ContractState {
  Unknown = 0,
  支払待ち = 1,
  自動請求待ち = 2,
  有効 = 3,
  ユーザーキャンセル = 4,
  システムキャンセル = 5,
  コンビニ支払待ち = 6,
  コンビニ支払期限切れ = 7,
  期限切れ = 8,
}

/**
 * 画像種別のEnum
 * https://dev.azure.com/cosat/Circlems/_git/WebcatalogPublic?path=/Src/SupportTools/MapGeneratorCore/MapGeneratorCore.Core/Model/%E7%94%BB%E5%83%8F%E7%A8%AE%E5%88%A5%E3%82%B3%E3%83%BC%E3%83%89enum.cs&version=GBqa
 */
export enum CircleImageTypeEnum {
  Unknown = 0,
  申込用サークルカット = 1,
  WebCatalog用サークルカット = 2,
  宣伝用画像 = 3,
  表紙用画像 = 4,
  企業カット = 5,
  企業バナー = 6,
  動画サークルカット = 7,
  プロフィール写真 = 11, // プロフィール写真は開催ごとではなくった
  コスプレ写真 = 12,
  プレゼント写真 = 13,
  販売計画書別紙画像用 = 21,

  Pixiv新着画像 = 107,
  Pixiv新着小説 = 108,
  Nico新着画像 = 109,
  書店新着画像 = 110,
  Clipstudio新着画像 = 111
}
export type GetMapTilesLastModifiedResult = components["schemas"]["MapTilesLastModifiedResponseBody"]

/**
 * WebAPI には型定義がない
 */
export type GetMapTilesResult = {
  geo: Array<MapGeoData>
}

/**
 * カタログマップの座標計算のデータ
 * WebAPI には型定義がない
 */
export type MapGeoData = {
  /** 例 "1/Ａ01a" (day/block+space)。ホール絞り込み時の識別に利用する。 */
  key?: string
  /** 例 "7" (半角数字文字列)。ホール絞り込み時の所属判定キー。 */
  hall?: string
  cut: number[]
  space: number[]
  wcid: number | undefined
}

/**
 * 企業マップの座標計算のデータ
 * WebAPIに型定義がない (components["schemas"]["JsonNode"] になっている)
 */
export type BoothMapGeoData = {
  boothId: number
  cut: number[]
  spaces: Array<number[]>
}

/**
 * Blobに保存されているJSONの形式
 * WebAPIに型定義がない (components["schemas"]["JsonNode"] になっている)
 */
export type GetMapSettingsResult = {
  AreaCode: number
  AreaName: MapArea
  DisplayName: string
  Ratio: number
  TableSize: number
  SpaceWidth: number
  SpaceHeight: number
  MinimapWidth: number
  MinimapHeight: number
  BigmapWidth: number
  BigmapHeight: number
}

/**
 * WebAPIに型定義がない (components["schemas"]["JsonNode"] になっている)
 */
export type BoothPublicLink = {
  linkUrl: string
  imageUrl: string | undefined
  updatedAt: number
}

/**
 * WebAPIに型定義がない (components["schemas"]["JsonNode"] になっている)
 */
export type Booth = {
  boothId: number
  name: string
  kana: string
  japaneseColumn: string
  imageUrl: string
  createdAt: number
  updatedAt: number
  displayNumber: number
  description: string
  product: string
  links: Array<BoothPublicLink>
  area: ComikeMapType
  geo: BoothMapGeoData
}

/**
 * WebAPIに型定義がない (components["schemas"]["JsonNode"] になっている)
 */
export type BoothMapSettings = {
  areaCode: number
  areaName: MapArea
  displayName: string
  ratio: number
  tableSize: number
  spaceWidth: number
  spaceHeight: number
  minimapWidth: number
  minimapHeight: number
  bigmapWidth: number
  bigmapHeight: number
}

export type FavoriteBooth = components["schemas"]["BoothFavorite"]

/**
 * WebAPIに型定義がない (components["schemas"]["JsonNode"] になっている)
 */
export type GetBoothBaseResult = {
  status: number
  boothInfo: {
    areas: Array<Area>
    booths: Array<Booth>
    sizeSettings: Array<BoothMapSettings>
  }
}

export type BoothBaseState = {
  /**
   * 更新日時 この値が一致していれば WebAPI 問い合わせを抑制するために使う
   */
  lastModified: number
  acceptLanguage: string
  boothInfo: {
    areas: Array<Area>
    booths: Array<Booth> // Map にするかどうか要素数的に微妙なライン
    sizeSettings: Array<BoothMapSettings>
  }
}

/**
 * BoothBaseState を「1階層だけ深く」readonly 化した型 (#33449 再発防止)。
 * トップおよびコンテナ (Array) の in-place 変更 (.sort/.push 等) は型エラーにするが、
 * 要素 (Booth/Area/BoothMapSettings) 自体の書き換えは型上許容する。
 * ReadonlyEventBaseState と同じ設計方針 (要素への Proxy 化コストを避ける)。
 */
export type ReadonlyBoothBaseState = {
  readonly lastModified: BoothBaseState["lastModified"]
  readonly acceptLanguage: BoothBaseState["acceptLanguage"]
  readonly boothInfo: {
    readonly areas: ReadonlyArray<Area>
    readonly booths: ReadonlyArray<Booth>
    readonly sizeSettings: ReadonlyArray<BoothMapSettings>
  }
}

// ComikeWebCatalog\Emer.IntwcApi\Emer.IntwcApi.Core\Models\V2\V2CircleSearchRequest.cs
// public SortOrderBy GetSortOrderBy() =>
//   sort switch
//   {
//       Sort新着順 => SortOrderBy.Date,
//       Sort配置背番号 => SortOrderBy.Space,
//       Sort検索エンジンランキング => SortOrderBy.RankingScore,
//       _ => SortOrderBy.Unknown
//   };
// ComikeWebCatalog\Emer.IntwcApi\Emer.IntwcApi.Core\Models\V2\V3CircleSearchRequest.cs
// public const int Sort新着順 = 0;
// public const int Sort配置背番号 = 1;
// public const int Sort検索エンジンランキング = 2;
// public const int Sortお気に入り番号 = 3
export const SortOptions = {
  Date: 0,
  Space: 1,
  RankingScore: 2,
  Favorite: 3,
} as const;

export type SortOptionsType = typeof SortOptions.Date | typeof SortOptions.Space | typeof SortOptions.RankingScore | typeof SortOptions.Favorite

export type ExternalServiceItem = {
  serviceId: number
  serviceName: string
  imagePath: string
}

// from "~/types/entity/cWebCatalog".ts より移動
export type CircleSearchParam = /* GetSearchCircleRequest & event_id を保持したくない */ {
  // event_id: number // 検索上は必須だが State内部の管理上 undefined
  keyword?: string
  day?: number | undefined
  area: string // 未選択は空文字で表現する
  blocks?: /* string | */ string[] | undefined // API は配列専用
  genres?: /* string | */ string[] | undefined
  lastUpdateAt?: number
  circleName?: string | undefined
  canUseElectronicPayment?: boolean // 電子決済を受け付けるか
  sort: SortOptionsType | undefined // ユーザ入力のみを取り扱う。undefined時の判定条件は resolveCircleSort に依存する。 0: 更新日時(新着順) 1: 配置背番号 2: 検索エンジンランキング順
  rankingScoreThreshold?: number | undefined // 検索エンジンランキングの閾値
  count?: number | undefined
  page: number

  wcid?: number
  // favorite?: boolean | undefined // お気に入りのみに絞り込んだ検索 をAPIとしては実装していないためここに入れない
  /**
   * 絞り込み対象 お気に入り色番号 (以前の実装とは異なり 0 を特別扱いしない。 undefined は条件として扱わない)
   */
  favoriteColor?: number | undefined
  /**
   * お気に入りメモを検索
   */
  favoriteMemo?: boolean | undefined
  // 詳細検索オプション
  isSearchCircleName?: boolean | undefined
  isSearchAuthor?: boolean | undefined
  isSearchProductInfo?: boolean | undefined
  view: ViewMode | undefined
}
// export type SearchType = "DEFAULT" | "DETAILS"

/**
 * マップ座標
 */
export type MapCoordinate = {
  x: number
  y: number
}

export type ComikeMapGeoData = {
  area: Area
  geoDataByWcid: Map<number | undefined, MapGeoData>
  /**
   * area/day 単位の全 geo 配列。
   * geoDataByWcid は wcid を持つエントリのみなので、ホール絞り込み (geo[].hall による判定) と
   * bounding rect 算出では全エントリを走査できるこの配列を利用する。
   */
  geoAll: MapGeoData[]
}
/**
 * カタログマップの座標
 */
export type CircleArea = {
  space: string
  northWest: MapCoordinate
  southEast: MapCoordinate
}
export type CircleListSplitHallList = {
  circles: Array<{
    day: number
    hall: MapArea | undefined
    circle: Circle
  }>
  // count: number // circles.length で取得可能
  maxCount: number
  page: number
}
export type HallMarker = {
  hallType: ComikeMapType
  lat: number
  lng: number
  isCircleArea: boolean
  isBoothArea: boolean
}
export type ComikeMapType = MapArea | "ALL"
export type EventDayList = Array<{
  day: number
  title: string
  hallList: Array<EventDayRow>
}>
export type EventDayRow =
{
  day?: number
  area: ComikeMapType // type Area に対して "ALL" を拡張した
  name?: string | null
  blocks?: string[] | null
  building?: string | null
}
export type ComikeMapSetting = {
  areaCode: number
  areaName: MapArea
  displayName: string
  ratio: number
  tableSize: number
  spaceWidth: number
  spaceHeight: number
  miniMapWidth: number
  miniMapHeight: number
  bigMapWidth: number
  bigMapHeight: number
}
export type MapBaseState = {
  /**
   * 更新日時 この値が一致していれば WebAPI 問い合わせを抑制するために使う
   */
  lastModified: number
  acceptLanguage: string
  /**
   * tile256/minimap/minimapSizeSettings.json を反映したもの
   */
  comikeMapSettingList: Array<ComikeMapSetting>
  /**
   * tile256/minimap/e123-Day1.json すべての日・ホールの内容を結合したもの
   */
  comikeMapGeoDataList: Array<ComikeMapGeoData>
}

/**
 * MapBaseState を「1階層だけ深く」readonly 化した型 (#33449 再発防止)。
 * トップおよびコンテナ (Array) の in-place 変更 (.sort/.push 等) は型エラーにするが、
 * 要素 (ComikeMapSetting/ComikeMapGeoData) 自体の書き換えは型上許容する。
 * ReadonlyEventBaseState / ReadonlyBoothBaseState と同じ設計方針 (要素への Proxy 化コストを避ける)。
 */
export type ReadonlyMapBaseState = {
  readonly lastModified: MapBaseState["lastModified"]
  readonly acceptLanguage: MapBaseState["acceptLanguage"]
  readonly comikeMapSettingList: ReadonlyArray<ComikeMapSetting>
  readonly comikeMapGeoDataList: ReadonlyArray<ComikeMapGeoData>
}
/**
 * Mapに存在する人たちの属性
 */
// export type ComikeMapAttributeActorType = "CIRCLE" | "BOOTH" // HallMarker から算出していたが直接渡す方がよい

// const bounds = $l.latLngBounds([northWest, southEast]); に渡せるような LatLngTuple 2要素の配列
export type RenderPosition = LatLngTuple[]

export type MapSearchCategory = "CIRCLE" | "BOOTH"
export type MapSelectParam = {
  day?: number | undefined
  hall?: ComikeMapType | undefined
  wcid?: number | undefined
  lastUpdateAt?: number | undefined
  /**
   * 企業の boothId (開催Idがついている方)
   */
  boothId?: number | undefined
  /**
   * このカテゴリは 検索のためであり その時の MAP 表示には直接関係しない
   */
  category: MapSearchCategory // 非選択状態を許可しない
  /**
   * 検索キーワード
   */
  keyword?: string | undefined
  /**
   * クラシックWebCatalog で ジャンルコード に対する中心地に遷移するパターンがある
   * 2025年2月現在 国際版に機能実装されていないがパラメータ引継ぎのみ実装している
   */
  genreCode?: string | undefined
}

export type QueryFavorite = {
  tab: number // tab は必須。初期値は 1
  sort?: string
  /**
   * 絞り込み対象 お気に入り色番号 (0 の場合はすべて)
   */
  favorite?: number
  day?: number
  wcid?: number | undefined
}

/**
 * 検索条件として指定可能な日付
 * 特に 0 を送信しないために実装している。UI管理上 0 を扱うことがある
 */
export type SearchParamDayType = 1 | 2 | 3 | 4 | 5 | 6 | 7 | undefined;
export const NormalizeDay = (day: number | undefined): SearchParamDayType => {
  switch (day) {
    case 1:
    case 2:
    case 3:
    case 4:
    case 5:
    case 6:
    case 7:
      return day;
    default:
      return undefined;
  }
};

/**
 * State 用のクラス genres に配列利用すると失敗することがあったために作成した
 * State はアプリケーション内で伝播するもののため設定時には 全項目必須 として扱い 未設定項目が洗い出せるようにする
 */
export type CircleSearchParamForState = {
  // event_id: number // state では event_id を保持しない
  keyword: string | undefined
  day: SearchParamDayType
  area: string
  blocksCsv: string // blocks をカンマで結合した値
  genresCsv: string // genres をカンマで結合した値
  canUseElectronicPayment: boolean | undefined // 電子決済を受け付けるか
  favoriteColor: number | undefined // 絞り込み対象 お気に入り色番号
  favoriteMemo: boolean | undefined
  sort: SortOptionsType | undefined // 0: 更新日時(新着順) 1: 配置背番号 2: 検索エンジンランキング順
  rankingScoreThreshold: number | undefined // 検索エンジンランキングの閾値
  // count: number
  page: number
  wcid: number | undefined
  // prev: "catalog" | "favorite" | undefined
  // 詳細検索オプション
  isSearchCircleName: boolean
  isSearchAuthor: boolean
  isSearchProductInfo: boolean
  view: ViewMode | undefined
}

export type BoothSearchParamForState = {
  keyword?: string
  searchBoothName?: boolean
  searchExhibitionDetails?: boolean
}

export type PrintParam = {
  printType: PrintType
  printEachHall: boolean
  days: number[]
  areas: ComikeMapType[]
  /**
   * ホール番号絞り込み (例 ["1","2"])。
   * 空配列 = 従来通りエリア単位。
   * 値ありで該当ホールのみ 1 ページ 1 ホールでズーム印刷する。
   */
  halls: string[]
  colors: number[]
  prnColor: boolean
  prnGenre: boolean
  prnMemo: boolean
  /**
   * A3 マップ＋リスト／マップ＋カットでマップとリストを接続線でつなぐか。
   */
  prnLines: boolean
  /**
   * リスト印刷のメモ欄で文字サイズ自動調整 (autofit) を行うか。既定はオフ。
   */
  prnMemoFit: boolean
  /**
   * マッピングリスト行のメモ欄想定行数 (MEMO_LINES_MIN〜MEMO_LINES_MAX)。行高と1頁件数に使う。
   * @see ~/utils/printListLayout
   */
  memoLines: number
  locale?: string
}

/**
 * Map表示のコンポーネント間で引き渡されるプロパティ
 * 以前以下のように指定されていたもの
 * day: {
 *   type: Number,
 *   default: 1
 * },
 * mapType: {
 *   type: String as () => ComikeMapType,
 *   default: "ALL"
 * },
 * hallAttrActor: {
 *   type: Object as () => HallMarker | undefined,
 *   default: undefined
 * },
 */
export type MapContextType = {
  day: number | undefined // booth の場合は undefined
  // mapType: ComikeMapType // HallMarker に含まれる
  hallMarker: HallMarker
};

export type {
  CircleListWithPage,
};

// Leaflet と合わせるための型
export type LatLngTuple = [number, number, number?]

export type CutMode = "web" | "book" | "video";
/** サークル一覧のカットサイズ。large: 180x256 / small: 133x189 */
export type CutSize = "large" | "small";
/** list: web/book/detail。favoriteMemo は /user/favorites 専用 */
export const ViewModes = ["web", "book", "detail", "favoriteMemo"] as const;
export type ViewMode = typeof ViewModes[number];
export const isViewMode = (v: string | undefined): v is ViewMode => ViewModes.some(x => x === v);
export type SortDirection = "asc" | "desc";
export type SortResult = {
  cutMode?: CutMode;
  cutSize?: CutSize;
  viewMode?: ViewMode;
  showExtraContentCount?: number;
  /** サークルPR の表示オン・オフ。オフでも showExtraContentCount (件数) は保持する */
  showExtraContent?: boolean;
  days?: string[];
  genres?: string[];
  areas?: string[];
  blocks?: string[];
  /** 設定パネルで編集中のキーワード。適用するまで検索条件 (circleSearchState) には反映しない */
  keyword?: string;
  sortType: SortOptionsType; // 表示上かならず何かしらのソート指定が含まれていなければならない。
  sortDirection: SortDirection;
  /** UI 用 percent (0–100)。0 / 未設定は API へ undefined */
  rankingScoreThresholdPercent?: number;
};

/**
 * CircleListにてブロック、事故サークル、クロネコヤマトスペースを判断するための型
 */
export type ExtraSpaceCircle = {
  type: number | undefined
  wcid?: number | null | undefined
  position?: string | null
  day?: string | null
  block?: string | null
  space?: string | null
  genre?: string | null
  is2SP?: boolean | null
  isMain?: boolean | null
  imgNo?: number | null // 連続している場合に画像の並び順制御が必要な場合に利用。現状は荷受け表示向け
};

/**
 * CircleList で 1 要素として表示しうる 3 種類の union
 */
export type CircleViewSpace = Circle | ICircleJyunbikaiWithHallNumber | ExtraSpaceCircle;

/**
 * 一覧の ExtraSpace / 準備会スペースではなく、wcid を持つ実サークル (Circle) かを判定する型ガード
 */
export function isCircleDetail(space: CircleViewSpace): space is Circle {
  return typeof space.wcid === "number";
}